import React, { useState } from 'react';

import { Button, Group, NumberInput, Stack, Switch, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import KiloAlert from 'components/KiloAlert';
import KiloInput from 'components/KiloInput';
import { useAuth } from 'context/auth-context';
import useCreateCommittedClubProgram from 'hooks/committedClub/useCreateCommittedClubProgram';
import useUpdateCommittedClubProgram from 'hooks/committedClub/useUpdateCommittedClubProgram';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate } from 'react-router-dom';
import { CheckInProgramList } from 'types';
import { DEFAULT_PROGRAM_NAME } from 'utils/checkInProgram';
import { getUpdatedFieldKeys, SelectorMap } from 'utils/compare-obj-functions';
import { parseDateTimeToDate } from 'utils/date-functions';
import notificationHandler from 'utils/notifications';

import ProgramReadyModal from './ProgramReadyModal';

const FORM_ID = 'committed-club-settings-form';
const DEFAULT_THRESHOLD = 12;
// New programs start visible to athletes; an edit shows the stored value.
const DEFAULT_ATHLETE_VISIBILITY = true;

interface FormValues {
  name: string;
  currentThreshold: number;
  athleteVisibilityEnabled: boolean;
}

// Field selectors for computing changed_fields on update (Mixpanel). They read the
// same keys off the stored program and the submitted form values.
const CHANGED_FIELD_SELECTORS: SelectorMap = {
  name: (v: FormValues) => v.name,
  current_threshold: (v: FormValues) => v.currentThreshold,
  athlete_visibility_enabled: (v: FormValues) => v.athleteVisibilityEnabled,
};

interface ISettingsDrawerProps {
  opened: boolean;
  closeHandler: () => void;
  program?: CheckInProgramList;
  permission?: boolean;
}

const monthName = (date: Date): string => date.toLocaleString('default', { month: 'long' });

const SettingsDrawer = ({
  opened,
  closeHandler,
  program,
  permission = true,
}: ISettingsDrawerProps): JSX.Element => {
  const navigate = useNavigate();
  const { session, refreshMe } = useAuth();
  const createMutation = useCreateCommittedClubProgram();
  const updateMutation = useUpdateCommittedClubProgram();
  const { trackCheckInProgramCreated, trackCheckInProgramSettingsUpdated } = useStaffTracking();
  const [programReadyOpen, setProgramReadyOpen] = useState(false);

  const isEdit = !!program;

  const form = useForm<FormValues>({
    initialValues: {
      name: DEFAULT_PROGRAM_NAME,
      currentThreshold: DEFAULT_THRESHOLD,
      athleteVisibilityEnabled: DEFAULT_ATHLETE_VISIBILITY,
    },
    validate: {
      name: value => (value.trim() ? null : 'Program name is required'),
      currentThreshold: value => (value >= 1 ? null : 'Check-ins required must be at least 1'),
    },
  });

  useShallowEffect(() => {
    if (!opened) return;
    form.setValues({
      name: program?.name ?? DEFAULT_PROGRAM_NAME,
      currentThreshold: program?.currentThreshold ?? DEFAULT_THRESHOLD,
      athleteVisibilityEnabled: program?.athleteVisibilityEnabled ?? DEFAULT_ATHLETE_VISIBILITY,
    });
  }, [opened, program]);

  // Gym-local today: the backend stages the threshold against the gym's timezone,
  // so browser-local dates would misjudge day-1 vs mid-month across boundaries.
  const today = parseDateTimeToDate(new Date().toISOString(), session?.gym?.timezone);
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const thresholdChanged = isEdit && form.values.currentThreshold !== program?.currentThreshold;
  const showMidMonthAlert = thresholdChanged && today.getDate() !== 1;

  const trimmedName = form.values.name.trim();
  const headerName = trimmedName || 'your';
  const header = isEdit ? `Configure your gym's ${headerName} program` : `Set up your ${headerName} program`;

  const handleClose = (): void => {
    closeHandler();
    form.clearErrors();
  };

  const goToTracking = (): void => {
    setProgramReadyOpen(false);
    navigate('/check-in-program/tracking');
  };

  const handleSubmit = (values: FormValues): void => {
    const { errorNotification } = notificationHandler();

    if (isEdit && program) {
      updateMutation.mutate(
        {
          checkInProgramId: program.checkInProgramId,
          name: values.name,
          currentThreshold: values.currentThreshold,
          athleteVisibilityEnabled: values.athleteVisibilityEnabled,
        },
        {
          onSuccess: response => {
            trackCheckInProgramSettingsUpdated(
              response.data.checkInProgramId,
              response.data.currentThreshold,
              response.data.athleteVisibilityEnabled,
              getUpdatedFieldKeys(program, values, CHANGED_FIELD_SELECTORS),
            );
            refreshMe();
            handleClose();
            navigate('/check-in-program/tracking');
          },
          onError: error => errorNotification('Update failed', error.response?.data.message),
        },
      );
    } else {
      createMutation.mutate(
        {
          name: values.name,
          currentThreshold: values.currentThreshold,
          athleteVisibilityEnabled: values.athleteVisibilityEnabled,
        },
        {
          onSuccess: response => {
            trackCheckInProgramCreated(
              response.data.checkInProgramId,
              response.data.currentThreshold,
              response.data.athleteVisibilityEnabled,
            );
            refreshMe();
            handleClose();
            setProgramReadyOpen(true);
          },
          onError: error => errorNotification('Creation failed', error.response?.data.message),
        },
      );
    }
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={handleClose}
        size='lg'
        title={<Title order={3}>{header}</Title>}
        footer={
          <Group position='right'>
            <Button variant='subtle' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              form={FORM_ID}
              type='submit'
              disabled={!permission || !trimmedName}
              loading={createMutation.isLoading || updateMutation.isLoading}
            >
              {isEdit ? 'Save changes' : 'Save program'}
            </Button>
          </Group>
        }
      >
        <form id={FORM_ID} onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <KiloInput
              label='Program name'
              placeholder='e.g. Committed Club'
              required
              disabled={!permission}
              variantStyle='outlinedWithLabel'
              withAsterisk={false}
              {...form.getInputProps('name')}
            />
            <NumberInput
              label='Check-ins required per month'
              min={1}
              disabled={!permission}
              {...form.getInputProps('currentThreshold')}
            />
            {showMidMonthAlert && (
              <KiloAlert variantStyle='warning'>
                {`Changes will apply starting ${monthName(nextMonth)} — ${monthName(
                  today,
                )} is already in progress.`}
              </KiloAlert>
            )}
            <Switch
              label='Athlete visibility'
              disabled={!permission}
              {...form.getInputProps('athleteVisibilityEnabled', { type: 'checkbox' })}
            />
            <Text size='xs' color='dimmed'>
              Active, on-hold, and trial athletes are automatically enrolled. On-hold athletes receive no
              notifications while on hold.
            </Text>
          </Stack>
        </form>
      </Drawer>
      <ProgramReadyModal opened={programReadyOpen} onGoToTracking={goToTracking} />
    </>
  );
};

export default SettingsDrawer;
